import { useState } from "react"
import { ImageOff } from "lucide-react"
import { NUTRITION_QUEUE_WINDOWS, type NutritionQueueImage } from "@nova-ai/shared"
import { formatDateTime } from "../../lib/format-date"
import { NutritionHelp } from "./nutrition-help"

export function NutritionImage({
  url,
  label,
  className,
  compact = false,
}: {
  url: string | null
  label: string
  compact?: boolean
  className?: string
}) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null)
  if (!url || failedUrl === url) {
    return (
      <div className={className}>
        <div className="flex aspect-square h-full w-full flex-col items-center justify-center gap-2 rounded-lg border border-border bg-muted p-2 text-center text-xs text-muted-foreground">
          <ImageOff className={compact ? "size-4" : "size-6"} aria-hidden="true" />

          <span className={compact ? "sr-only" : undefined}>Image unavailable</span>
        </div>
      </div>
    )
  }
  return (
    <img
      className={className}
      src={url}
      alt={label}
      loading="lazy"
      onError={() => setFailedUrl(url)}
    />
  )
}

export function NutritionImages({
  images,
  period,
  observedAt,
  onOpen,
}: {
  images: NutritionQueueImage[]
  period: "24h" | "7d"
  observedAt: string
  onOpen: (receiptId: number, store: string, sku: string) => void
}) {
  return (
    <section
      aria-label="Recent images"
      className="min-w-0 rounded-2xl border border-border bg-card shadow-card max-sm:rounded-lg"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 px-5 pt-4 max-sm:px-3.5 max-sm:pt-3.5">
        <h2 className="text-[14px] font-semibold">Recent images</h2>

        <div className="flex items-center gap-1">
          <p className="text-[12px] text-muted-foreground">
            Newest images recorded in GCS. Select one to open its receipt.
          </p>

          <NutritionHelp label="About recent images">
            Recorded transitions, not proof of present freshness or available bytes. Opening an
            image shows its exact receipt, store/SKU, current selection, and retained publication
            evidence.
          </NutritionHelp>
        </div>
      </div>

      {images.length === 0 ? (
        <p className="px-5 py-4 text-[13px] text-muted-foreground">
          No retained image transitions in the last {NUTRITION_QUEUE_WINDOWS[period].label}.
        </p>
      ) : (
        <div className="flex snap-x snap-proximity scroll-px-5 gap-3 overflow-x-auto px-5 pt-3 pb-4 [scrollbar-width:none] max-sm:scroll-px-3.5 max-sm:px-3.5">
          {images.map((image) => (
            <button
              key={image.transitionId}
              type="button"
              className="grid w-32 flex-none snap-start gap-1.5 rounded-lg text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              onClick={() => onOpen(image.receiptId, image.store, image.sku)}
              title={`${image.selectedNow ? "Selected now" : "No longer selected"} · freshness unverified · ${formatDateTime(image.recordedAt)}`}
            >
              <NutritionImage
                url={image.url}
                label={`Nutrition image for ${image.sku}, ${image.store}`}
                className="aspect-square w-32 rounded-lg border border-border bg-white object-contain"
              />

              <span className="grid text-[12px]">
                <span className="font-semibold">{image.store}</span>

                <span className="truncate text-muted-foreground">
                  {image.productName ?? image.sku}
                </span>

                <time dateTime={image.recordedAt} className="tabular-nums text-muted-foreground">
                  {`${new Date(image.recordedAt).toLocaleTimeString("en-GB", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })} · ${imageAge(image.recordedAt, observedAt)}`}
                </time>

                {!image.selectedNow && image.imageId !== null && (
                  <span className="text-warning">No longer selected</span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </section>
  )
}

function imageAge(recordedAt: string, observedAt: string) {
  const minutes = Math.max(0, Math.floor((Date.parse(observedAt) - Date.parse(recordedAt)) / 60000))
  if (minutes < 1) {
    return "just now"
  }
  if (minutes < 60) {
    return `${minutes} min ago`
  }
  if (minutes < 1440) {
    return `${Math.floor(minutes / 60)} h ago`
  }
  return `${Math.floor(minutes / 1440)} days ago`
}
